<template>
  <div>

    <modal
      :title="$t('titles.edit_venues', venueLocationProducts.length)"
      :submit="submit"
      :submitting="submitting"
      :submit-button="!readonly"
      :close-label="readonly ? $t('titles.close') : $t('titles.cancel')"
      ref="vlpModal"
    >

      <v-alert v-if="isBulkEdit" color="secondary" variant="tonal" class="mb-6">
        You are editing multiple venues. Only the fields that you fill out below will be updated. To leave a field unchanged, simply leave it blank.
      </v-alert>

      <div v-if="loading" class="text-center mb-6">
        <v-progress-circular indeterminate />
      </div>

      <div v-if="!loading">

        <v-row>

          <v-col>

            <o-select
              :label="$t('titles.entry_hours_before_event')"
              :items="effectiveTimeOffsetOptions"
              v-model.number="formState.entryHoursBeforeEvent"
              clearable
              :readonly="readonly"
              placeholder="Use product settings"
              persistent-placeholder
            />

          </v-col>

          <v-col>

            <o-select
              :label="$t('titles.exit_hours_after_event')"
              :items="effectiveTimeOffsetOptions"
              v-model.number="formState.exitHoursAfterEvent"
              clearable
              :readonly="readonly"
              placeholder="Use product settings"
              persistent-placeholder
            />

          </v-col>

          <v-col>

            <o-text-field
              :label="$t('titles.default_inventory')"
              type="number"
              min="0"
              max="10000"
              step="1"
              v-model.number="formState.defaultInventory"
              :clearable="isBulkEdit"
              :readonly="readonly"
              :placeholder="isBulkEdit ? $t('titles.unchanged') : null"
              persistent-placeholder
            />

          </v-col>

          <v-col>

            <template v-if="!readonly">

              <o-switch
                v-if="!isBulkEdit"
                label="Live"
                v-model="formState.live"
                :readonly="readonly"
              />

              <o-select
                v-if="isBulkEdit"
                label="Live"
                inline
                v-model="formState.live"
                :items="[{value: true, title: 'Yes'}, {value: false, title: 'No'}]"
                :placeholder="isBulkEdit ? 'Unchanged' : null"
                persistent-placeholder
                clearable
              />

            </template>

            <v-chip
              v-if="readonly"
              label
              :prepend-icon="formState.live ? '$enabled' : '$disabled'"
              :color="formState.live ? 'enabled' : 'disabled'"
            >{{ formState.live ? 'Live' : 'Not Live' }}</v-chip>

          </v-col>

        </v-row>

        <v-checkbox
          v-if="isBulkEdit"
          v-model="formState.leaveBulkCostUnchanged"
          :label="$t('titles.unchanged')"
        />

        <v-table v-if="!isBulkEdit || (isBulkEdit && !formState.leaveBulkCostUnchanged)">
          <thead>
            <tr>
              <th v-if="canSeeIds" scope="col">{{ $t('titles.id') }}</th>
              <th :colspan="readonly ? 4 : 5" scope="col">{{ $t('titles.cost_structure') }}</th>
            </tr>
          </thead>
          <tfoot>
            <tr>
              <td v-if="!readonly" :colspan="canSeeIds ? 6 : 5" class="text-end">
                <v-btn icon :disabled="formState.venueTiers.length >= 5" density="compact" @click="addTier(null, null, 100)">
                  <v-icon icon="$create" />
                  <v-tooltip activator="parent" location="bottom" :text="$t('titles.add_tier')" />
                </v-btn>
              </td>
            </tr>
          </tfoot>
          <tbody>
            <tr v-for="venueTier, index in formState.venueTiers">
              <td v-if="canSeeIds">{{ venueTier.ID }}</td>
              <td class="pt-6" style="max-width: 120px">
                <o-text-field
                  :label="$t('titles.cost')"
                  prefix="$"
                  type="number"
                  density="compact"
                  :readonly="readonly"
                  :rules="formRules.venueTiers.tierPrice"
                  v-model.number="venueTier.tierPrice"
                />
              </td>
              <td class="pt-6" style="max-width: 100px">
                <o-text-field
                  :label="$t('titles.from')"
                  type="number"
                  density="compact"
                  suffix="%"
                  readonly
                  :model-value="index === 0 ? 0 : formState.venueTiers[index-1].tierInventoryPercentage"
                />
              </td>
              <td class="pt-6" style="max-width: 100px">
                <o-text-field
                  :label="$t('titles.to')"
                  type="number"
                  density="compact"
                  suffix="%"
                  min="0"
                  max="100"
                  step="1"
                  :readonly="readonly || index === lastTierIndex"
                  :rules="[...formRules.venueTiers.tierInventoryPercentage, (value) => index === lastTierIndex ? (value === 100 ? true : $t('Last tier must be 100')) : (value < formState.venueTiers[index + 1].tierInventoryPercentage ? true : $t('validations.too_high'))]"
                  v-model.number="venueTier.tierInventoryPercentage"
                />
              </td>
              <td>of inventory</td>
              <td v-if="!readonly" class="text-end">
                <v-btn
                  :disabled="formState.venueTiers.length < 2"
                  icon
                  density="compact"
                  @click="deleteTier(index)"
                >
                  <v-icon icon="$delete" />
                  <v-tooltip activator="parent" location="bottom" :text="$t('titles.remove_tier')" />
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>

      </div>

    </modal>

  </div>
</template>

<script setup>

import { computed, inject, onBeforeMount, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

// models
import Venue from '../../../models/Venue'
import { VenueLocationProduct } from '../../../models/VenueLocationProduct'
// import { UpdateVlpRequest, UpdateVlpRequestVenueTier } from '../models/VenueLocationProduct'

// misc
import { closeDialog } from 'vue3-promise-dialog'
import NotificationService from '../../../services/NotificationService'
import timeOffsetOptions from '../../../utils/timeOffsetOptions'
import { dollarAmount, integer, minValue, maxValue, positive, required } from '../../../validation'

const props = defineProps({

  locationID: {
    type: Number,
    required: true,
  },

  venueLocationProductIDs: {
    type: Array /*<Number>*/,
    required: true,
  },

  readonly: {
    type: Boolean,
    default: false,
  },

})

const $can = inject('$can')
const canSeeIds = $can('seeids')
const { t } = useI18n()

const vlpModal = ref('vlpModal') // template ref
const isBulkEdit = props.venueLocationProductIDs.length > 1

const venueLocationProducts = ref([])

/**
 * Initialization
 */

const loading = ref(false)

onBeforeMount(() => {

  loading.value = true

  // make sure we have the most current version of these VLPs
  Venue.fetchLocationVenues(props.locationID)
    .then((result) => {

      venueLocationProducts.value = VenueLocationProduct.query()
        .whereIdIn(props.venueLocationProductIDs)
        .with('venueTiers', (query) => {
          query.orderBy('tierInventoryPercentage')
        })
        .get()

      if (isBulkEdit) {
        addTier(null, null, 100)
        formState.entryHoursBeforeEvent = -1 // "Unchanged"
        formState.exitHoursAfterEvent = -1
      } else {
        // We're editing a single VLP. Copy its config into the form.
        const vlp = venueLocationProducts.value[0]
        formState.live = vlp.live
        formState.entryHoursBeforeEvent = vlp.entryHoursBeforeEvent
        formState.exitHoursAfterEvent = vlp.exitHoursAfterEvent
        formState.live = vlp.live
        formState.mode = vlp.venueTiers.length ? 'tiered' : 'flat'
        formState.defaultInventory = vlp.defaultInventory
        if (vlp.venueTiers.length) {
          vlp.venueTiers.forEach((venueTier) => {
            addTier(venueTier.ID, venueTier.tierPrice, venueTier.tierInventoryPercentage * 100)
          })
        } else {
          addTier(null, vlp.defaultPrice, 100)
        }
      }

    })
    .catch((e) => {
      console.error(e)
      NotificationService.error(t('alerts.couldnt_load_venues'))
    })
    .finally(() => {
      loading.value = false
    })

})

/**
 * Form state and rules
 */

const formState = reactive({
  entryHoursBeforeEvent: null,
  exitHoursAfterEvent: null,
  defaultInventory: null,
  live: null,
  leaveBulkCostUnchanged: isBulkEdit ? true : null,
  venueTiers: [],
})

const formRules = {
  defaultInventory: [
    required(),
    integer(),
    maxValue(10000),
  ],
  venueTiers: {
    tierPrice: [
      required(),
      dollarAmount(),
      positive(),
    ],
    tierInventoryPercentage: [
      required(),
      integer(),
      minValue(1),
      maxValue(100),
    ],
  },
}

const effectiveTimeOffsetOptions = computed(() => {
  const options = []
  if (isBulkEdit) {
    options.push({value: -1, title: 'Unchanged'})
  }
  options.push(...timeOffsetOptions)
  return options
})

function addTier(ID = null, tierPrice = null, tierInventoryPercentage = null) {
  formState.venueTiers.push({
    key: Math.random(),
    ID: ID,
    tierPrice: tierPrice,
    tierInventoryPercentage: tierInventoryPercentage,
  })
}

function deleteTier(index) {
  formState.venueTiers.splice(index, 1)
  const lastTier = formState.venueTiers.at(-1)
  if (lastTier.tierInventoryPercentage < 100) {
    lastTier.tierInventoryPercentage = 100
  }
}

const lastTierIndex = computed(() => formState.venueTiers.length - 1)

/**
 * Submission
 */

const submitting = ref(false)

async function submit() {

  await vlpModal.value.modalForm.validate()

  if (!vlpModal.value.modalForm.isValid) {
    return
  }

  submitting.value = true

  const payload /*: UpdateVlpRequest*/ = {}

  if (formState.entryHoursBeforeEvent !== -1) {
    payload.entryHoursBeforeEvent = formState.entryHoursBeforeEvent
  }

  if (formState.exitHoursAfterEvent !== -1) {
    payload.exitHoursAfterEvent = formState.exitHoursAfterEvent
  }

  if (!([null, undefined, ''].includes(formState.defaultInventory))) {
    payload.defaultInventory = formState.defaultInventory
  }

  if (formState.live === true || formState.live === false) {
    payload.live = formState.live
  }

  const promises = venueLocationProducts.value.map(vlp => {

    // if the venueTiers are being changed, then we need to build a custom
    // sub-payload for each VLP (because each VLP will have different tier IDs)

    const vlpPayload = {...payload}

    // TODO: in single-edit mode, reuse original IDs

    if (!isBulkEdit || (isBulkEdit && !formState.leaveBulkCostUnchanged)) {

      vlpPayload.venueTiers = formState.venueTiers.map((formVenueTier, i /*: number*/)/*: UpdateVlpRequestVenueTier */=> {
        
        const output = {
          tierPrice: formVenueTier.tierPrice,
          tierInventoryPercentage: formVenueTier.tierInventoryPercentage / 100,
        }

        if (isBulkEdit) {
          // The tier rows on the form don't correspond to specific rows in the
          // venue_tiers table.
          if (vlp.venueTiers[i]) {
            output.id = vlp.venueTiers[i].ID
          }
        } else {
          if (formVenueTier.ID) {
            output.id = formVenueTier.ID
          }
        }

        return output

      })

    }

    return VenueLocationProduct.saveExisting(
      props.locationID,
      vlp.ID,
      vlpPayload
    )

  })

  Promise.all(promises)
    .then(() => {
      NotificationService.success(t('alerts.venues_updated', props.venueLocationProductIDs.length))
      closeDialog(null)
    })
    .catch((e) => {
      console.error(e)
      NotificationService.error(t('alerts.couldnt_update_venues', props.venueLocationProductIDs.length))
    })
    .finally(() => {
      submitting.value = false
    })

}

</script>
